/**
 * Subdomain-based study routing utilities.
 *
 * Each study is accessible via its own subdomain:
 * - abc-101.localhost -> Study ABC-101
 * - xyz-202.example.com -> Study XYZ-202
 * - test-101.clienta.sagarmatha.ai -> Study TEST-101 (multi-level domain)
 *
 * The study ID is extracted from the subdomain automatically.
 */

import { getRuntimeConfig } from './runtimeConfig'

/**
 * Get the base domain from the current URL.
 * E.g., "app.example.com" -> "example.com"
 * E.g., "abc-101.localhost" -> "localhost"
 * E.g., "test-101.clienta.sagarmatha.ai" -> "clienta.sagarmatha.ai"
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname

  // Use backend-configured base domain if available (supports 3+ level domains
  // like clienta.sagarmatha.ai where studies are test-101.clienta.sagarmatha.ai)
  const runtimeConfig = getRuntimeConfig()
  if (runtimeConfig?.baseDomain && hostname.endsWith(runtimeConfig.baseDomain)) {
    return runtimeConfig.baseDomain
  }

  // Handle localhost
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return 'localhost'
  }

  // Handle IP addresses (no subdomain possible)
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname
  }

  // Fallback: extract base domain (last two parts)
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return parts.slice(-2).join('.')
  }

  return hostname
}

/**
 * Get the subdomain from the current URL.
 * E.g., "abc-101.localhost" -> "abc-101"
 * E.g., "abc-101.app.example.com" -> "abc-101"
 * E.g., "localhost" -> null (no subdomain)
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname
  const baseDomain = getBaseDomain()

  // No subdomain if hostname equals base domain
  if (hostname === baseDomain) {
    return null
  }

  // Extract subdomain
  const subdomain = hostname.replace(`.${baseDomain}`, '')

  // Ignore common non-study subdomains
  const ignoredSubdomains = ['www', 'app', 'api', 'auth', 'admin']
  if (ignoredSubdomains.includes(subdomain.toLowerCase())) {
    return null
  }

  return subdomain || null
}

/**
 * Convert a study ID to a valid subdomain.
 * - Lowercase
 * - Replace invalid characters with hyphens
 * - Remove leading/trailing hyphens
 */
export function studyIdToSubdomain(studyId: string): string {
  return studyId
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Convert a subdomain back to the original study ID.
 * Note: This is a best-effort conversion since we lose case information.
 */
export function subdomainToStudyId(subdomain: string): string {
  // The subdomain is already lowercase, return as-is
  // The backend should do case-insensitive matching
  return subdomain.toUpperCase()
}

/**
 * Get the current study ID from the subdomain.
 * Returns null if on the main domain (no study selected).
 */
export function getCurrentStudyFromSubdomain(): string | null {
  const subdomain = getSubdomain()
  if (!subdomain) {
    return null
  }
  return subdomainToStudyId(subdomain)
}

/**
 * Build a URL for a specific study using subdomain routing.
 */
export function buildStudyUrl(studyId: string, path: string = '/'): string {
  const baseDomain = getBaseDomain()
  const subdomain = studyIdToSubdomain(studyId)
  const protocol = window.location.protocol
  const port = window.location.port

  // Build the URL
  let url = `${protocol}//${subdomain}.${baseDomain}`

  // Add port if not default
  if (port && port !== '80' && port !== '443') {
    url += `:${port}`
  }

  // Add path
  if (path && path !== '/') {
    url += path.startsWith('/') ? path : `/${path}`
  }

  return url
}

/**
 * Build a URL for the main app (no study selected).
 */
export function buildMainAppUrl(path: string = '/'): string {
  const baseDomain = getBaseDomain()
  const protocol = window.location.protocol
  const port = window.location.port

  let url = `${protocol}//${baseDomain}`

  if (port && port !== '80' && port !== '443') {
    url += `:${port}`
  }

  if (path && path !== '/') {
    url += path.startsWith('/') ? path : `/${path}`
  }

  return url
}

/**
 * Navigate to a study by changing to its subdomain.
 * Default path is '/' which lets the app redirect to the first configured page.
 */
export function navigateToStudy(studyId: string, path: string = '/'): void {
  const url = buildStudyUrl(studyId, path)
  window.location.href = url
}

/**
 * Navigate back to the main app (studies list).
 */
export function navigateToMainApp(path: string = '/studies'): void {
  const url = buildMainAppUrl(path)
  window.location.href = url
}

/**
 * Check if we're currently on a study subdomain.
 */
export function isOnStudySubdomain(): boolean {
  return getSubdomain() !== null
}

/**
 * Get configuration for the current environment.
 */
export function getSubdomainConfig() {
  return {
    baseDomain: getBaseDomain(),
    currentSubdomain: getSubdomain(),
    currentStudyId: getCurrentStudyFromSubdomain(),
    isOnStudySubdomain: isOnStudySubdomain(),
  }
}
